import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, combineLatest, fromEvent, merge, of, timer } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, scan, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';

import * as AuthSelectors from '../../auth/store/auth.selectors';
import { NotificationTickets } from '../../shared/api/queries/notifications-queries';
import { SortOrder, TicketSearchInput, TicketSortField, TicketStatus } from '../../shared/api/types/GraphQL';
import { LanguageService } from '../../shared/services/language.service';
import { NotificationStatePreference } from '../../shared/services/preferences/notification-state-preference.service';
import { NotificationSeenState, withIds } from '../../shared/utils/notification-state-storage.util';
import { AppNotification, NotificationTicketRow, buildNotificationRows } from './notification-rows.util';

export type { AppNotification, NotificationKind } from './notification-rows.util';

/**
 * ~60s, per the design canon ("polling — no live indicators") and the
 * NBO-2207 discovery agreement: the backend has no subscriptions or push, so
 * the bell polls the existing tickets query. Never go below 30s.
 */
const POLL_INTERVAL_MS = 60_000;

/**
 * The panel shows the newest assigned open actions, not a full history — one
 * page is plenty, and it keeps every poll cheap.
 */
const PAGE_SIZE = 20;

interface NotificationIdentity {
  /** AdminUser id as stored in the auth slice. */
  userIdRaw: string;
  /** The same id, numeric, for the assignedToAdminUserIds variable. */
  adminUserId: number;
  /** Tickets are tenant-scoped: a tenant switch restarts the poll. */
  tenantKey: string;
}

/**
 * Live data source for the top-bar notifications bell (NBO-2207, Action Hub
 * assigned-action alerts). The panel itself was built in NBO-2331 and shipped
 * hidden; this service turned it on.
 *
 * Mechanics worth knowing before touching it:
 * - Polling starts lazily with the first `list()` subscription and stops when
 *   the bell unsubscribes (shareReplay refCount) — auth pages never poll.
 * - A failed poll is SILENT: the last good rows stay, no snackbar. A 60s
 *   retry loop must never spam the user (same isolation rule as facets).
 * - "New" is an id diff against the persisted seen/read sets, not a timestamp
 *   comparison — the schema has no assignedAt (see notification-rows.util).
 * - Seen/read state is per user + tenant on the account (NBO-2518, the
 *   `notifications` preference), behind NotificationStatePreference. A mark
 *   made before the account's rows are in shows at once and is written when
 *   they arrive; another tab's marks and the late load re-derive the rows.
 */
@Injectable({ providedIn: 'root' })
export class NotificationsService {
  /**
   * Master visibility switch for the bell — true since NBO-2207. Kept as a
   * flag (not deleted) so the bell can be taken out of production with a
   * one-line change if the polling load ever needs revisiting.
   */
  readonly enabled = true;

  /** Snapshot of the currently displayed tickets, for the mark methods. */
  private latestTickets: NotificationTicketRow[] = [];
  private identity: NotificationIdentity | null = null;

  /**
   * Set when the user opens the Action Hub before the poll has landed (a cold
   * load straight onto that route subscribes the bell at the same moment), so
   * the first result is still covered by the visit.
   */
  private actionHubVisitPending = false;

  /** Bumped after every seen/read write so the shared pipeline re-derives. */
  private readonly stateBump$ = new BehaviorSubject<void>(undefined);

  private readonly identity$ = combineLatest([
    this.store.select(AuthSelectors.selectUserId),
    this.store.select(AuthSelectors.selectActiveTenant)
  ]).pipe(
    map(([userId, tenant]): NotificationIdentity | null => {
      const numericId = Number(userId);
      if (!userId || Number.isNaN(numericId) || !tenant?.id) return null;
      return { userIdRaw: String(userId), adminUserId: numericId, tenantKey: String(tenant.id) };
    }),
    distinctUntilChanged((a, b) => a?.userIdRaw === b?.userIdRaw && a?.tenantKey === b?.tenantKey)
  );

  private readonly rows$: Observable<AppNotification[]> = this.identity$.pipe(
    switchMap((identity) => {
      this.identity = identity;
      this.latestTickets = [];
      if (!identity) return of([] as AppNotification[]);

      // A tab hidden for hours must not keep polling; on refocus the merge
      // fires an immediate catch-up tick instead of waiting out the timer.
      const visibilityRegained$ = fromEvent(document, 'visibilitychange').pipe(
        filter(() => document.visibilityState === 'visible')
      );
      const tickets$ = merge(timer(0, POLL_INTERVAL_MS), visibilityRegained$).pipe(
        filter(() => document.visibilityState !== 'hidden'),
        switchMap(() => this.fetchAssigned(identity.adminUserId)),
        // null = failed poll → keep the last good list, silently.
        scan((last, next) => next ?? last, [] as NotificationTicketRow[]),
        tap((tickets) => {
          this.latestTickets = tickets;
          this.settleActionHubVisit();
        })
      );

      // Rows carry already-resolved strings, so the derivation re-runs when
      // either language level moves (UI language for meta/time, content
      // language for the ticket titles) — the NBO-2287 pure-pipe lesson,
      // applied at the source.
      const language$ = merge(this.translate.onLangChange, this.languageService.observeContentLanguage()).pipe(
        startWith(null)
      );

      return combineLatest([tickets$, this.stateBump$, language$]).pipe(
        map(([tickets]) =>
          buildNotificationRows(tickets, this.seenState.read(), {
            resolveTitle: (titles) => this.languageService.resolveLocalizedString(titles),
            translate: (key, params) => this.translate.instant(key, params),
            now: new Date()
          })
        )
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  constructor(
    private apollo: Apollo,
    private store: Store,
    private translate: TranslateService,
    private languageService: LanguageService,
    private seenState: NotificationStatePreference
  ) {
    // The mark methods write per user + tenant, and both come from here. The
    // rows pipeline resolves identity too, but only while something is
    // subscribed to it — the Action Hub marks rows read whether or not the
    // bell is on screen.
    this.identity$.subscribe((identity) => (this.identity = identity));
    // The state also changes from outside this service: the account's rows
    // arriving, another tab marking rows read.
    this.seenState.changes().subscribe(() => this.stateBump$.next());
  }

  /** Assigned open actions, newest first; polling runs while subscribed. */
  list(): Observable<AppNotification[]> {
    return this.rows$;
  }

  /**
   * Marks everything currently listed as seen. The badge counts UNSEEN and
   * clears when the panel opens; rows keep their unread brand-edge until
   * individually read.
   */
  markAllSeen(): void {
    const ids = this.latestTickets.map((t) => t.id);
    this.updateState((state) => ({ ...state, seenIds: withIds(state.seenIds, ids) }));
  }

  /**
   * The user opened the Action Hub themselves, so everything assigned to them
   * is SEEN — the badge answers "something new arrived", and they are looking
   * at the list (NBO-2207 review). Rows stay UNREAD until acted on: a glance
   * at the hub must not erase the marks on actions still waiting for work.
   */
  markAllSeenOnActionHubVisit(): void {
    this.actionHubVisitPending = true;
    this.settleActionHubVisit();
  }

  markAllRead(): void {
    const ids = this.latestTickets.map((t) => t.id);
    this.updateState((state) => ({
      seenIds: withIds(state.seenIds, ids),
      readIds: withIds(state.readIds, ids)
    }));
  }

  /** Reading a row implies having seen it. */
  markRead(id: string): void {
    this.updateState((state) => ({
      seenIds: withIds(state.seenIds, [id]),
      readIds: withIds(state.readIds, [id])
    }));
  }

  private settleActionHubVisit(): void {
    if (!this.actionHubVisitPending || !this.latestTickets.length) return;
    this.actionHubVisitPending = false;
    this.markAllSeen();
  }

  private updateState(update: (state: NotificationSeenState) => NotificationSeenState): void {
    if (!this.identity) return;
    this.seenState.update(update);
    this.stateBump$.next();
  }

  private fetchAssigned(adminUserId: number): Observable<NotificationTicketRow[] | null> {
    const input: TicketSearchInput = {
      assignedToAdminUserIds: [adminUserId],
      statuses: [TicketStatus.Open, TicketStatus.InProgress],
      sortInputs: [{ field: TicketSortField.CreatedAt, order: SortOrder.Desc }],
      page: 1,
      offset: PAGE_SIZE
    };
    return this.apollo
      .query<{ tickets: { items: NotificationTicketRow[] } }>({
        query: NotificationTickets,
        variables: { input },
        fetchPolicy: 'no-cache'
      })
      .pipe(
        map((result) => result.data?.tickets?.items ?? []),
        catchError(() => of(null))
      );
  }
}
